/**
 * Relocation Kit Builder Widget (TelePortal Setup R1)
 *
 * Presents the tailored Relocation Kit generated by Brain.Learning.
 * Users can:
 *  - Remove optional tasks with a direct X button (required+blocking tasks are locked)
 *  - Restore removed tasks from a collapsible "Removed" section
 *  - Rebuild: shows an inline confirmation explaining the kit will be regenerated
 *  - Activate: accepts the kit (with or without removals) and advances the guide
 *  - Back: navigates to the previous guide step
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';
import { useLocale } from '@tonnie/locale';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
} from '@tonnie/ui';
import {
  Home,
  Trash2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Star,
  Users,
  ChevronDown,
  ChevronUp,
  Loader2,
} from '@tonnie/assets';
import { motion, AnimatePresence } from 'framer-motion';
import type { WidgetProps } from '@tonnie/copilot';

import {
  type RelocationKit,
  type KitTask,
  TRACK_ICONS,
  trackVisual,
  COMPLEXITY_BADGE,
} from './relocation-kit/types';
import { SummaryCard } from './relocation-kit/SummaryCard';
import { TrackSection } from './relocation-kit/TrackSection';
import { MemberSection } from './relocation-kit/MemberSection';
import { RemovedTaskRow } from './relocation-kit/RemovedTaskRow';

// ═══════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════

const BUILD_TIMEOUT_MS = 30_000;

export function RelocationKitBuilder({ onComplete, config }: WidgetProps) {
  const { t } = useLocale();
  const kit: RelocationKit | null =
    ((config as Record<string, unknown>)?.relocationKit as RelocationKit | null) ??
    ((config as Record<string, unknown>)?.kit as RelocationKit | null) ??
    null;

  const [isTimedOut, setIsTimedOut] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (kit) return;
    const timeoutTimer = setTimeout(() => {
      if (mountedRef.current) setIsTimedOut(true);
    }, BUILD_TIMEOUT_MS);
    return () => clearTimeout(timeoutTimer);
  }, [kit]);

  // The user's tailoring of the auto-built kit IS their work — every
  // checkbox, every removed task. Persist it so navigating away mid-
  // build doesn't force them to recurate the same dozen tasks. View
  // ephemerality (expanded/collapsed sections, modal-confirm state)
  // stays local — they're not "progress."
  //
  // CRITICAL: every mutator below MUST go through `patchPersisted`'s
  // functional form. The previous wrapper `setRemovedCodes` captured
  // `persisted.removedCodes` from its render's closure, and the
  // `useCallback(..., [])` empty-deps on `handleRemoveTask` /
  // `handleRestoreTask` froze them to the FIRST-render wrapper — whose
  // closure had the initial empty array. Result: every X click read
  // `removedCodes = Set([])` and wrote `[code]`, clobbering any
  // previously-removed task. The user could only "remove" one task at
  // a time, and after a reload the server returned the original list
  // because of the second bug — `pendingPatchRef` was being merged
  // OUTSIDE the setLocal updater, so the remote PATCH shipped `{}`
  // (see `use-widget-state.ts` for that fix).
  const [persisted, patchPersisted] = useWidgetState<{
    removedCodes: string[];
    viewMode: 'track' | 'member';
  }>('relocation_kit_builder', { removedCodes: [], viewMode: 'track' });
  const removedCodes = useMemo(() => new Set(persisted.removedCodes), [persisted.removedCodes]);
  const viewMode = persisted.viewMode;

  // Mount + persist-change tracing. Pre-fix this widget would write
  // `{removedCodes: ['only-the-latest']}` on each X-click (stale closure
  // collapsing the array) AND ship an empty server PATCH (see
  // `use-widget-state.ts`'s pendingPatchRef bug). On reload the kit
  // reverted because the server's stored slice was untouched. This log
  // makes the symptom visible — `mountedCount` should match what the
  // user actually removed, both immediately and after leave-and-return.
  useEffect(() => {
    console.log('[CP:Widget:Kit] persisted', {
      removedCount: persisted.removedCodes.length,
      removedCodes: persisted.removedCodes,
      viewMode: persisted.viewMode,
      kitTaskCount: kit?.tasks.length ?? 0,
    });
  }, [persisted.removedCodes, persisted.viewMode, kit?.tasks.length]);
  const setViewMode = useCallback(
    (next: 'track' | 'member') => patchPersisted({ viewMode: next }),
    [patchPersisted]
  );
  // UI ephemerality below — not persisted.
  const [expandedTracks, setExpandedTracks] = useState<Set<string>>(
    new Set(['legal', 'travel', 'setup'])
  );
  const [showRemoved, setShowRemoved] = useState(false);
  // Rebuild confirmation: null = idle, 'confirm' = awaiting user confirmation
  const [rebuildState, setRebuildState] = useState<'idle' | 'confirm'>('idle');

  const activeTasks = useMemo(() => {
    if (!kit) return [];
    return kit.tasks.filter((task) => !removedCodes.has(task.task_code));
  }, [kit, removedCodes]);

  const removedTasks = useMemo(() => {
    if (!kit) return [];
    return kit.tasks.filter((task) => removedCodes.has(task.task_code));
  }, [kit, removedCodes]);

  const tasksByTrack = useMemo(() => {
    const groups: Record<string, KitTask[]> = {};
    for (const task of activeTasks) {
      if (!groups[task.track]) groups[task.track] = [];
      groups[task.track].push(task);
    }
    return groups;
  }, [activeTasks]);

  const tasksByMember = useMemo(() => {
    const groups: Record<string, KitTask[]> = {};
    for (const task of activeTasks) {
      const key = task.household_member_name ?? t('teleportal.kit.defaultMember');
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    }
    return groups;
  }, [activeTasks, t]);

  const canRemoveTask = useCallback((task: KitTask) => {
    return !(task.is_required && task.is_blocking);
  }, []);

  const getDependencyWarning = useCallback(
    (taskCode: string) => {
      if (!kit) return null;
      const dependents = kit.dependency_graph
        .filter((e) => e.from === taskCode)
        .map((e) => e.to)
        .filter((code) => !removedCodes.has(code));
      if (dependents.length === 0) return null;
      return t('teleportal.kit.dependencyWarning').replace('{{count}}', String(dependents.length));
    },
    [kit, removedCodes, t]
  );

  const handleRemoveTask = useCallback(
    (code: string) => {
      patchPersisted((prev) => {
        if (prev.removedCodes.includes(code)) {
          // Already removed — no-op. Keeps the patch idempotent so
          // accidental double-clicks don't churn the autosave debounce.

          console.log('[CP:Widget:Kit] removeTask SKIP already-removed', { code });
          return {};
        }
        const next = [...prev.removedCodes, code];

        console.log('[CP:Widget:Kit] removeTask', {
          code,
          prevCount: prev.removedCodes.length,
          nextCount: next.length,
        });
        return { removedCodes: next };
      });
    },
    [patchPersisted]
  );

  const handleRestoreTask = useCallback(
    (code: string) => {
      patchPersisted((prev) => {
        if (!prev.removedCodes.includes(code)) {
          console.log('[CP:Widget:Kit] restoreTask SKIP not-in-removed', { code });
          return {};
        }
        const next = prev.removedCodes.filter((c) => c !== code);

        console.log('[CP:Widget:Kit] restoreTask', {
          code,
          prevCount: prev.removedCodes.length,
          nextCount: next.length,
        });
        return { removedCodes: next };
      });
    },
    [patchPersisted]
  );

  const handleRebuildRequest = useCallback(() => {
    setRebuildState('confirm');
  }, []);

  const handleRebuildConfirm = useCallback(() => {
    patchPersisted({ removedCodes: [] });
    setRebuildState('idle');
    onComplete?.({ action: 'rebuild', rebuildTarget: 'processing_start' });
  }, [onComplete, patchPersisted]);

  const handleRebuildCancel = useCallback(() => {
    setRebuildState('idle');
  }, []);

  const handleAccept = useCallback(() => {
    onComplete?.({
      action: 'accept',
      modifications: {
        removed_task_codes: Array.from(removedCodes),
      },
      kit,
    });
  }, [onComplete, removedCodes, kit]);

  if (!kit) {
    if (isTimedOut) {
      return (
        <Card
          className="border-destructive/30"
          data-testid="widget-relocation_kit"
          data-state="timed-out"
        >
          <CardContent className="py-10 text-center space-y-4">
            <AlertTriangle className="w-10 h-10 mx-auto text-destructive-accessible/60" />
            <div>
              <h4 className="text-sm font-semibold mb-1">{t('teleportal.kit.buildFailed')}</h4>
              <p className="text-xs text-muted-foreground max-w-sm mx-auto">
                {t('teleportal.kit.buildFailedHint')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setIsTimedOut(false);
                onComplete?.({ action: 'rebuild', rebuildTarget: 'processing_start' });
              }}
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              {t('teleportal.kit.retry')}
            </Button>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card
        className="border-primary/20 bg-primary/5"
        data-testid="widget-relocation_kit"
        data-state="loading"
      >
        <CardContent className="py-10 text-center space-y-4">
          <Loader2 className="w-8 h-8 text-primary-accessible mx-auto animate-spin" />
          <p className="text-sm text-muted-foreground">{t('teleportal.kit.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  const complexityInfo = COMPLEXITY_BADGE[kit.complexity.level] ?? COMPLEXITY_BADGE.standard;
  const documentsNeeded = activeTasks.filter(
    (task) => task.requires_document && !task.vault_linked
  ).length;
  const documentsLinked = activeTasks.filter((task) => task.vault_linked).length;
  const isModified = removedCodes.size > 0;

  return (
    <div className="space-y-4" data-testid="widget-relocation_kit" data-state="ready">
      {/* Kit Summary */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-bold">{t('teleportal.kit.title')}</h3>
              <p className="text-sm text-muted-foreground">
                {kit.metadata.origin} → {kit.metadata.destination}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={cn('text-xs font-medium px-2 py-1 rounded-full', complexityInfo.variant)}
              >
                {t(complexityInfo.labelKey)}
              </span>
              {isModified && (
                <Badge variant="outline" className="text-tone-amber border-tone-amber-line">
                  {t('teleportal.kit.modified')} (-{removedCodes.size})
                </Badge>
              )}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3">
            <SummaryCard
              label={t('teleportal.kit.tasks')}
              value={activeTasks.length}
              icon={CheckCircle2}
            />
            <SummaryCard
              label={t('teleportal.kit.docsNeeded')}
              value={documentsNeeded}
              icon={FileText}
            />
            <SummaryCard
              label={t('teleportal.kit.docsLinked')}
              value={documentsLinked}
              icon={Star}
            />
            <SummaryCard
              label={t('teleportal.kit.members')}
              value={kit.metadata.household_member_count}
              icon={Users}
            />
          </div>
        </CardContent>
      </Card>

      {/* Track Overview Cards */}
      <div className="grid grid-cols-3 gap-3">
        {kit.tracks.map((track) => {
          const trackTasks = tasksByTrack[track.track_code] ?? [];
          const Icon = TRACK_ICONS[track.track_code] ?? Home;
          const visual = trackVisual(track.track_code);
          const milestones = kit.milestones.filter((m) => m.track === track.track_code);

          return (
            <Card
              key={track.track_code}
              className={cn('border-l-4', visual?.edgeColor ?? 'border-l-border')}
            >
              <CardContent className="pt-3 pb-2">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={cn('w-4 h-4', visual?.color ?? 'text-muted-foreground')} />
                  <span className="text-sm font-semibold">
                    {visual ? t(visual.nameKey) : t(track.track_name_key)}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('teleportal.kit.taskCount', { count: trackTasks.length })} ·{' '}
                  {t('teleportal.kit.milestoneCount', { count: milestones.length })}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Editing hint */}
      <p className="text-xs text-muted-foreground flex items-center gap-1.5 px-1">
        <Trash2 className="w-3.5 h-3.5 shrink-0" />
        {t('teleportal.kit.editHint')}
      </p>

      {/* View Toggle + Task Lists */}
      <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'track' | 'member')}>
        <TabsList className="w-full">
          <TabsTrigger
            value="track"
            className="flex-1"
            data-testid="widget-relocation_kit-view-track"
          >
            {t('teleportal.kit.byTrack')}
          </TabsTrigger>
          <TabsTrigger
            value="member"
            className="flex-1"
            data-testid="widget-relocation_kit-view-member"
          >
            {t('teleportal.kit.byMember')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="track" className="mt-3 space-y-3">
          {kit.tracks.map((track) => (
            <TrackSection
              key={track.track_code}
              track={track}
              tasks={tasksByTrack[track.track_code] ?? []}
              onRemoveTask={handleRemoveTask}
              canRemove={canRemoveTask}
              getDependencyWarning={getDependencyWarning}
              isExpanded={expandedTracks.has(track.track_code)}
              onToggleExpand={() =>
                setExpandedTracks((prev) => {
                  const next = new Set(prev);
                  if (next.has(track.track_code)) next.delete(track.track_code);
                  else next.add(track.track_code);
                  return next;
                })
              }
              t={t}
            />
          ))}
        </TabsContent>

        <TabsContent value="member" className="mt-3 space-y-3">
          {Object.entries(tasksByMember).map(([memberName, tasks]) => (
            <MemberSection
              key={memberName}
              memberName={memberName}
              tasks={tasks}
              onRemoveTask={handleRemoveTask}
              canRemove={canRemoveTask}
              getDependencyWarning={getDependencyWarning}
              t={t}
            />
          ))}
        </TabsContent>
      </Tabs>

      {/* Removed Tasks — collapsible restore area */}
      <AnimatePresence>
        {removedTasks.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >
            <Card className="border-dashed border-muted-foreground/30">
              <button
                type="button"
                className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-muted/20 transition-colors"
                onClick={() => setShowRemoved((v) => !v)}
              >
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" />
                  {t('teleportal.kit.removedTasks')} ({removedTasks.length})
                </span>
                {showRemoved ? (
                  <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" />
                ) : (
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                )}
              </button>
              <AnimatePresence>
                {showRemoved && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="overflow-hidden"
                  >
                    <div className="px-4 pb-3 space-y-1.5">
                      {removedTasks.map((task) => (
                        <RemovedTaskRow
                          key={task.task_code}
                          task={task}
                          onRestore={handleRestoreTask}
                          t={t}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rebuild confirmation panel */}
      <AnimatePresence>
        {rebuildState === 'confirm' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.15 }}
          >
            <Card className="border-tone-amber-line bg-tone-amber-subtle">
              <CardContent className="py-4 px-4 space-y-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-tone-amber shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-tone-amber">
                      {t('teleportal.kit.rebuildConfirmTitle')}
                    </p>
                    <p className="text-xs text-tone-amber mt-1 leading-relaxed">
                      {t('teleportal.kit.rebuildConfirmDesc')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={handleRebuildCancel}>
                    {t('common.cancel')}
                  </Button>
                  <Button size="sm" onClick={handleRebuildConfirm} className="gap-1.5">
                    <RefreshCw className="w-3.5 h-3.5" />
                    {t('teleportal.kit.rebuildConfirm')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Action Bar */}
      <div className="flex items-center justify-between pt-2 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          onClick={handleRebuildRequest}
          disabled={rebuildState === 'confirm'}
          className="gap-1.5"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t('teleportal.kit.rebuild')}
        </Button>
        <Button
          size="sm"
          onClick={handleAccept}
          data-testid="widget-relocation_kit-confirm"
          className="bg-tone-green-solid hover:bg-tone-green-solid/90 text-tone-green-on-solid gap-1.5"
        >
          <CheckCircle2 className="w-3.5 h-3.5" />
          {isModified ? t('teleportal.kit.activateModified') : t('teleportal.kit.activate')}
        </Button>
      </div>
    </div>
  );
}
